import { useEffect, useState, createContext, useContext } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const cxGet = (path) =>
  axios.get(`${API_URL}/api${path}`, { withCredentials: true, headers: authHeaders() }).then(r => r.data);
export const cxPost = (path, body) =>
  axios.post(`${API_URL}/api${path}`, body || {}, { withCredentials: true, headers: authHeaders() }).then(r => r.data);
export const cxPut = (path, body) =>
  axios.put(`${API_URL}/api${path}`, body || {}, { withCredentials: true, headers: authHeaders() }).then(r => r.data);

export const fmtMoney = (n, cur) => `${cur ? cur + ' ' : ''}${Math.round(Number(n || 0)).toLocaleString()}`;
export const fmtNum = (n) => Number(n || 0).toLocaleString();
export const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
export const timeAgo = (d) => {
  if (!d) return '—';
  const t = new Date(String(d).replace(' ', 'T'));
  if (isNaN(t)) return String(d).slice(0, 10);
  const days = Math.floor((Date.now() - t.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

// Segment + dormancy visual language
// Monochrome navy→stone ramp — keeps the dashboard editorial and low-chroma
// (aligns with the app's single-accent design language).
export const MONO = ['#0A2540', '#3E4C59', '#5B6B7B', '#808B96', '#A9B0B8', '#CDD2D7'];

export const SEG_META = {
  'Champion':       { color: '#0A2540', bg: 'bg-[#0A2540] text-white' },
  'Loyal':          { color: '#3E4C59', bg: 'bg-[#3E4C59] text-white' },
  'Promising':      { color: '#5B6B7B', bg: 'bg-[#EDE9E1] text-[#44403C]' },
  'Cooling':        { color: '#808B96', bg: 'bg-[#EDE9E1] text-[#57534E]' },
  'At Risk':        { color: '#8A97A3', bg: 'bg-[#E4DED2] text-[#57534E]' },
  'Dormant':        { color: '#A9B0B8', bg: 'bg-[#DAD3C6] text-[#44403C]' },
  'Lost':           { color: '#C4C9CE', bg: 'bg-stone-200 text-stone-600' },
  'New / Inactive': { color: '#D9DEE3', bg: 'bg-[#FCFAF6] text-[#78716C] border border-[#EDE6D8]' },
};
export const segClass = (s) => (SEG_META[s]?.bg) || 'bg-[#FCFAF6] text-[#78716C] border border-[#EDE6D8]';

export const BUCKET_META = {
  active:       { label: 'Active (0-30d)',    color: '#0A2540' },
  cooling:      { label: 'Cooling (31-60d)',  color: '#4A5A6A' },
  at_risk:      { label: 'At Risk (61-90d)',  color: '#7A8794' },
  dormant:      { label: 'Dormant (91-180d)', color: '#A2AAB2' },
  lost:         { label: 'Lost (180d+)',      color: '#C4C9CE' },
  never_active: { label: 'Never Active',      color: '#E0E0E3' },
};

// Current dormancy spectrum — days since last REFILL (configurable per entity
// via CX Targets). This is the single source of truth used across Overview,
// Customers and reports. Ranges shown are the seeded defaults.
export const SPECTRUM_META = {
  active:  { label: 'Active',         range: '0–7d',   color: '#0A2540' },
  cooling: { label: 'Cooling',        range: '8–14d',  color: '#4A5A6A' },
  at_risk: { label: 'At Risk',        range: '15–29d', color: '#8A97A3' },
  lost:    { label: 'Lost',           range: '30d+',   color: '#C4C9CE' },
  never:   { label: 'Never refilled', range: '—',      color: '#E4DED2' },
};

// Customer tiers (Bronze/Silver/Gold) from avg 3-month refill revenue vs ARU target.
export const TIER_META = {
  Gold:   { color: '#C9A35B', bg: 'bg-[#C9A35B]/15 text-[#8A6D2B] border border-[#C9A35B]/40' },
  Silver: { color: '#8A97A3', bg: 'bg-[#8A97A3]/15 text-[#57534E] border border-[#8A97A3]/40' },
  Bronze: { color: '#C7A98B', bg: 'bg-[#C7A98B]/15 text-[#7A5A3A] border border-[#C7A98B]/40' },
};
export const tierClass = (t) => (TIER_META[t]?.bg) || 'bg-stone-100 text-stone-600';

export const StatCard = ({ label, value, sub, tone, testid }) => (
  <div className="bg-white border border-[#EDE6D8] rounded-xl p-5" data-testid={testid}>
    <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
    <p className={`text-2xl font-bold mt-2 ${tone || 'text-[#0A2540]'}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</p>
    {sub != null && <p className="text-xs text-[#78716C] mt-1">{sub}</p>}
  </div>
);

export const Panel = ({ title, actions, children, testid, className }) => (
  <div className={`bg-white border border-[#EDE6D8] rounded-xl p-6 ${className || ''}`} data-testid={testid}>
    {(title || actions) && (
      <div className="flex items-center justify-between gap-3 mb-4">
        {title && <h2 className="text-lg font-semibold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{title}</h2>}
        {actions}
      </div>
    )}
    {children}
  </div>
);

export const CxHeader = ({ title, subtitle, entityId, onEntity, right }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div>
      <h1 className="text-3xl font-bold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{title}</h1>
      {subtitle && <p className="text-sm text-[#78716C] mt-1">{subtitle}</p>}
    </div>
    <div className="flex items-end gap-3">
      <EntityScope value={entityId} onChange={onEntity} />
      {right}
    </div>
  </div>
);

// Shared, reactive entity scope — one source of truth for the whole CX
// workspace so switching entity in the header updates every tab live.
// Persists to localStorage.
const CxEntityCtx = createContext(null);

export function CxEntityProvider({ children }) {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const [entityId, setRaw] = useState(() => localStorage.getItem('cx_entity') || 'all');
  const setEntityId = (v) => { setRaw(v); localStorage.setItem('cx_entity', v); };
  return (
    <CxEntityCtx.Provider value={{ entityId: isSuper ? entityId : null, setEntityId, isSuper }}>
      {children}
    </CxEntityCtx.Provider>
  );
}

export function useCxEntity() {
  return useContext(CxEntityCtx) || { entityId: null, setEntityId: () => {}, isSuper: false };
}

export function EntityScope({ value, onChange }) {
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  useEffect(() => {
    if (user?.role !== 'super_admin') return;
    cxGet('/super-admin/entity-modules').then((rows) => setEntities(Array.isArray(rows) ? rows : [])).catch(() => {});
  }, [user]);
  if (user?.role !== 'super_admin') return null;
  return (
    <div className="space-y-1">
      <label className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Entity</label>
      <Select value={value || 'all'} onValueChange={onChange}>
        <SelectTrigger className="w-56 border-[#EDE6D8] rounded-lg h-10 bg-white" data-testid="cx-entity-scope">
          <SelectValue placeholder="All entities" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All entities</SelectItem>
          {entities.map((e) => (
            <SelectItem key={e.entity_id} value={String(e.entity_id)}>{e.entity_name || e.entity_id}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Coverage / refresh banner shown across CX pages.
export function CoverageBanner({ asOf, cached, total, onRefreshed }) {
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try {
      const r = await cxPost('/cx/metrics/refresh');
      toast.success(r.already_running ? 'A refresh is already running' : 'Metrics refresh started — this runs in the background');
      onRefreshed && onRefreshed();
    } catch (e) {
      toast.error('Could not start refresh');
    } finally { setBusy(false); }
  };
  const pct = total ? Math.round((cached / total) * 100) : 0;
  return (
    <div className="flex items-center justify-between gap-3 bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg px-4 py-2.5 mb-5" data-testid="cx-coverage-banner">
      <p className="text-xs text-[#78716C]">
        Metrics coverage <span className="font-semibold text-[#0A2540]">{fmtNum(cached)}/{fmtNum(total)}</span> ({pct}%)
        {asOf ? <> · as of {timeAgo(asOf)}</> : <> · not yet computed</>}
      </p>
      <button onClick={refresh} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A2540] hover:underline disabled:opacity-50" data-testid="cx-refresh-metrics">
        <ArrowsClockwise size={13} className={busy ? 'animate-spin' : ''} /> Refresh now
      </button>
    </div>
  );
}

export const CATEGORY_OPTS = ['billing', 'card', 'app', 'refill', 'complaint', 'request', 'other'];
export const PRIORITY_OPTS = ['low', 'medium', 'high', 'urgent'];
export const STATUS_OPTS = ['open', 'in_progress', 'pending', 'resolved', 'closed'];
export const statusClass = (s) => ({
  open: 'bg-[#0A2540] text-white', in_progress: 'bg-[#E4DED2] text-[#57534E]',
  pending: 'bg-stone-100 text-stone-600', resolved: 'bg-[#EDE9E1] text-[#57534E]',
  closed: 'bg-[#FCFAF6] text-[#78716C] border border-[#EDE6D8]',
}[s] || 'bg-stone-100 text-stone-700');
export const priorityClass = (p) => ({
  low: 'bg-stone-100 text-stone-500', medium: 'bg-[#EDE9E1] text-[#57534E]',
  high: 'bg-[#DAD3C6] text-[#44403C]', urgent: 'bg-[#0A2540] text-white',
}[p] || 'bg-stone-100 text-stone-600');
